import axios from 'axios';
import type {ClientOnErrorFunction} from 'react-router';
import {createBrowserRouter, isRouteErrorResponse, matchRoutes, redirect, useRouteError} from 'react-router';
import {RouterProvider} from 'react-router/dom';

import ErrorPage from '../partials/error-page';
import NotFoundPage from '../partials/not-found';
import {AppLogin} from '../application/app-login';
import {AppAdmin} from '../application/app-admin';
import {redirectTo, setRouter} from './partials/routing-util';
import {isLoggedIn, onEnvironmentChange} from '../../../helpers/session';
import {useIsLoggedIn} from '../../../hooks/use-session';
import {reportError} from '../../../helpers/error-reporter';
import {reportApiFailure} from '../../../helpers/axios-helper';
import {reloadForChunkError} from '../../../helpers/app-update';
import {PlaceholderTemplate} from '../partials/placeholder-template';
import {loginLoader} from '../../pages/guest/auth/login/login-loader';
import {terminalLoader} from '../../pages/admin/terminal/terminal-loader';

const loggedIn = (): Response | null => (isLoggedIn() ? null : redirect('/login'));

const NotFound = (): React.JSX.Element =>
    // re-renders when the user logs in or out (also in another tab)
    useIsLoggedIn() ? (
        <AppAdmin>
            <NotFoundPage />
        </AppAdmin>
    ) : (
        <NotFoundPage />
    );

const toError = (error: unknown): Error => {
    if (error instanceof Error) {
        return error;
    }

    return new Error(isRouteErrorResponse(error) ? `${error.status} ${error.statusText}` : String(error));
};

// errors in a loader or a lazy route import (a chunk gone after a deploy reloads the page, see `ErrorPage`); render
// errors in the pages are caught by the `ErrorHandler` in the layouts
const RouteErrorBoundary = (): React.JSX.Element => <ErrorPage error={toError(useRouteError())} />;

// the router reports each error once; render errors are left out, `onCaughtError` in app.tsx already reports those. A
// chunk that failed to load (after a deploy) reloads the page instead: called before the error boundary renders.
const onRouteError: ClientOnErrorFunction = (error, {location, pattern, errorInfo}) => {
    if (errorInfo || reloadForChunkError(error)) {
        return;
    }

    console.error(error);

    // a loader's request (e.g. the users of the login page): a failed request, not a bug of the route
    if (axios.isAxiosError(error)) {
        reportApiFailure('Load', error);
        return;
    }

    reportError(error, {section: 'router', values: {pathname: location.pathname, pattern}});
};

const router = createBrowserRouter([
    {
        path: '/',
        Component: AppLogin,
        HydrateFallback: PlaceholderTemplate,
        ErrorBoundary: RouteErrorBoundary,
        children: [
            {index: true, loader: () => redirect('/login')},
            {
                path: 'login',
                loader: loginLoader,
                lazy: async () => {
                    const {default: Component, LoginError} = await import('../../pages/guest/auth/login/login');

                    return {Component, ErrorBoundary: LoginError};
                },
            },
            {
                Component: AppAdmin,
                loader: loggedIn,
                children: [
                    {
                        path: 'terminal',
                        // in the route config, not the lazy module: the printers are fetched while the code loads
                        loader: terminalLoader,
                        lazy: async () => {
                            const {default: Component, TerminalError} =
                                await import('../../pages/admin/terminal/terminal');

                            return {Component, ErrorBoundary: TerminalError};
                        },
                    },
                ],
            },
            {path: '*', Component: NotFound},
        ],
    },
]);

setRouter(router);

// the environment changed (a switch, a session that ended, or another tab): a new login is needed; subscribed once
// here, next to the router, instead of in a component that StrictMode mounts twice
onEnvironmentChange(() => redirectTo('/login'));

/** The route a URL matches, as its full path pattern (e.g. `/terminal`, `/*` when none matches), for Bugsnag. */
export const resolveRoute = (url: URL): string => {
    const paths = (matchRoutes(router.routes, url.pathname) ?? [])
        .map(({route}) => route.path)
        .filter(path => path && '/' !== path);

    return `/${paths.join('/')}`;
};

export const Router = (): React.JSX.Element => <RouterProvider router={router} onError={onRouteError} />;
